'use client';

import { Translation } from '@/globals/translate/translations';

import { useSearchParams } from 'next/navigation';
import { ComponentRef, useEffect, useId, useRef, useState } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { submitButtonBox } from '@/uiComposites/interactive/callToAction';
import { textBlock } from '@/uiPrimitives/typography/text';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import {
	hiddenAboveMQ640px,
	hiddenBelowMQ640px,
} from '@/uiPrimitives/utility/display.css';
import { icon } from '@/uiPrimitives/media/icon';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { pageGrid } from '@/uiPrimitives/layout/pageGrid/pageGrid';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import {
	getSearchURL,
	searchTerm,
} from '@/globals/dataCollector/searchDataCollector/functions/getSearchURL';
import { getSearchState } from '@/globals/dataCollector/searchDataCollector/functions/getSearchState';
import { pushSearchResultEvent } from '@/globals/analytics/events/pushSearchEvent';
import { usePageHistory } from '@/globals/pageWrappers/usePageHistory';
import { useSearchHistory } from './useSearchHistory';

export const SearchFormClient = ({
	label,
	submitText,
}: {
	label: Translation<'What are you looking for?'>;
	submitText: Translation<'Search'>;
}) => {
	const router = useDOPRouter();
	const id = useId();
	const inputRef = useRef<ComponentRef<'input'>>(null);
	const searchParams = useSearchParams();
	const [pageHistory] = usePageHistory();
	const searchHistory = useSearchHistory();
	const firstRenderRef = useRef(true);

	const prevTerm = searchParams.get(searchTerm) || '';

	const handleSearch = (value: string) => {
		const urlsearch = new URLSearchParams(searchParams.toString());

		if (value === '') {
			urlsearch.delete(searchTerm);
		} else {
			urlsearch.set(searchTerm, value);
		}

		const searchState = getSearchState(urlsearch);
		if (prevTerm !== value) {
			searchState.facetSlug = 'all'; // Reset facetSlug to 'all' if the term changes
			searchState.page = 1;
		}
		const newUrl = getSearchURL(searchState);
		router.push(newUrl, { scroll: false });
		pushSearchResultEvent({
			searchState: searchState,
			searchHistory: searchHistory,
			pageHistory,
		});
	};

	const term = searchParams.get(searchTerm) || '';
	const [inputValue, setInputValue] = useState(term);

	useEffect(() => {
		setInputValue(term);
		if (!firstRenderRef.current) return;
		firstRenderRef.current = false;
		if (term !== '') return;
		inputRef.current?.focus();
	}, [term]);

	return (
		<div
			className={classes(
				pageGrid({}),
				box({ backgroundColor: 'negativeBlueEmphasis' }),
			)}
		>
			<form
				className={classes(
					stack({ gap: '3 | h1' }),
					box({
						paddingBlock: '5 | betweenParagraphs',
					}),
				)}
				onSubmit={(e) => {
					e.preventDefault();
				}}
			>
				<label
					htmlFor={id}
					className={classes(textBlock({ fontWeight: 'bold' }))}
					{...addTestSelector('uiSearchLabel')}
				>
					{label}
				</label>
				<div
					className={classes(
						shelf({ gridTemplateColumns: 'minmax(0, 1fr) auto' }),
						zIndex({ isolation: 'isolate' }),
					)}
				>
					<input
						ref={inputRef}
						type="search"
						name="search"
						autoCapitalize="none"
						autoComplete="off"
						id={id}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						className={classes(
							textBlock({}),
							box({
								paddingBlock: '3 | h1',
								paddingInline: indentSize,
								backgroundColor: 'negative',
								borderWidth: '1px',
								borderStyle: 'solid',
								borderColor: 'positive',
							}),
							zIndex({
								'zIndex:focusVisible': '1',
							}),
						)}
						{...addTestSelector('uiSearchInput')}
					/>
					<button
						type="submit"
						className={classes(submitButtonBox({}))}
						{...addTestSelector('uiSearchSubmitBtn')}
						onClick={() => {
							handleSearch(inputValue);
						}}
					>
						<span className={classes(textBlock({}), hiddenBelowMQ640px)}>
							{submitText}
						</span>
						<span className={classes(occupy({ inlineSize: 'capSize' }))}>
							<i
								role="img"
								aria-label={submitText}
								className={classes(
									icon({ icon: searchIcon, blockSize: '3 | h1' }),
									hiddenAboveMQ640px,
								)}
							/>
						</span>
					</button>
				</div>
			</form>
		</div>
	);
};
